import { getListingByAddress } from '@zg-rentals/rental-growth-api';
import type { GetListingByAddressRequestDto, GetListingByAddressResponseDto } from '@zg-rentals/rental-growth-api';
import type { FloorplanDto } from '@zg-rentals/ts-rental-growth-api-client';

// Actions
import { setActiveId } from './UIActions';

// Models
import Address from '../../models/Address';
import Comparables from '../../models/Comparables';

// Misc / utils
import { augmentError } from '../../utils/errors';
import constants from '../constants';
import errorConstants from '../../constants/ErrorConstants';
import type { AppThunkAddress, AppThunkPromise } from '../types';
import { emitErrorEvent } from '../../utils/analytics/ClickstreamEvents';

// ActionCreators... <verb><Noun>
export const getComparables = (params: GetListingByAddressRequestDto): AppThunkPromise => {
  return async (dispatch) => {
    const { data } = await dispatch(fetchListingByAddress(params));
    const userComps = dispatch(setComparables(data));

    return { userComps };
  };
};

export const getAddressAndComparables = (params: GetListingByAddressRequestDto): AppThunkPromise => {
  return async (dispatch) => {
    const { data } = await dispatch(fetchListingByAddress(params));
    const userAddress = dispatch(setAddress(data));
    const hasZestimate = Boolean(data.floorplans?.[0]?.zestimate?.rentZestimate);
    const hasCompsPrices = Boolean(data.similarFloorplans?.floorplans?.length);

    if (!hasCompsPrices && !hasZestimate) {
      throwNonCriticalError({
        message: "We don't have enough data for this address",
        code: errorConstants.RENT_ZESTIMATE_NOT_FOUND,
      });
    }

    if (hasCompsPrices) {
      await dispatch(setComparables(data));
    }

    return {
      userAddress,
    };
  };
};

export const fetchListingByAddress = (params: GetListingByAddressRequestDto) => {
  return async (dispatch, getState) => {
    try {
      const user = getState().user;
      const response = await getListingByAddress(params);

      if (!response.floorplans?.length) {
        emitErrorEvent({ user });
        throwNonCriticalError({
          message: 'We were unable to match this address',
          code: errorConstants.PROPERTY_DETAILS_NOT_FOUND,
        });
      }
      if (response.similarFloorplansError || !response.similarFloorplans?.floorplans?.length) {
        emitErrorEvent({ user });
      }
      if (!response.floorplans?.[0]?.zestimate?.rentZestimate) {
        emitErrorEvent({ user });
      }

      return { data: response };
    } catch (error) {
      augmentError(error, {
        action: 'Error in AddressActions#fetchListingByAddress',
      });

      throw error;
    }
  };
};

// Setters
export const setAddress = (data: GetListingByAddressResponseDto): AppThunkAddress => {
  return (dispatch) => {
    const addressData = {
      ...data.floorplans![0],
      geo: data.geo,
      marketSummary: data.marketSummary,
    };
    const modeledAddressData = new Address(addressData);

    dispatch({
      type: constants.SET_ADDRESS,
      payload: modeledAddressData,
    });

    if (modeledAddressData.listingId) {
      dispatch(setActiveId(modeledAddressData.listingId));
    }

    return modeledAddressData;
  };
};

export const setComparables = (data: GetListingByAddressResponseDto) => {
  return (dispatch, getState) => {
    const similarFloorplans = data.similarFloorplans;
    if (!similarFloorplans) {
      // Clear any previously loaded comparables so stale results don't linger
      dispatch({
        type: constants.SET_COMPARABLES,
        payload: {
          min: null,
          max: null,
          items: [],
          userPropertyIdx: null,
        },
      });

      return [];
    }

    const { floorplans, listingRanking, lowestPriceIncluded: min, highestPriceIncluded: max } = similarFloorplans;

    const compsItems = floorplans.map((comp: FloorplanDto) => {
      return new Comparables(comp);
    });

    const userProperty = getState().address;
    compsItems.splice(listingRanking, 0, userProperty);
    const userPropertyIdx = compsItems.findIndex((comp) => comp.isUsersComp);

    dispatch({
      type: constants.SET_COMPARABLES,
      payload: {
        min,
        max,
        items: compsItems,
        userPropertyIdx,
      },
    });

    dispatch(setActiveId(userProperty.listingId));

    return compsItems;
  };
};

// Errors
const throwNonCriticalError = ({ message, code }: { message: string; code: string }) => {
  throw {
    nonCriticalError: true,
    errorCode: code,
    errorMsg: message,
  };
};
